import { DAILY_CYCLE, DAILY_REWARDS } from "../../../shared/daily.js";
import { VIRTUAL_HEIGHT, VIRTUAL_WIDTH } from "../../config";
import { drawSceneBackground, drawSceneTitle, fillRoundedRect, strokeRoundedRect } from "../../utils/draw";
import type { Button } from "../systems/Buttons";
import { backButton } from "../systems/Buttons";
import { MenuScene } from "./MenuScene";
import type { GameContext, Scene } from "./Scene";

const CLAIM_BTN = { x: VIRTUAL_WIDTH / 2 - 110, y: 330, w: 220, h: 48 };
const CELL_W = 76;
const CELL_H = 76;

// Positions of the 7 reward cells: a 4-then-3 grid.
function cellRect(i: number): { x: number; y: number } {
  if (i < 4) {
    const startX = (VIRTUAL_WIDTH - (4 * CELL_W + 3 * 8)) / 2;
    return { x: startX + i * (CELL_W + 8), y: 110 };
  }
  const j = i - 4;
  const startX = (VIRTUAL_WIDTH - (3 * CELL_W + 2 * 8)) / 2;
  return { x: startX + j * (CELL_W + 8), y: 110 + CELL_H + 12 };
}

export class DailyScene implements Scene {
  private claiming = false;
  private entered = false;
  private toast = "";
  private toastT = 0;
  private pulse = 0;

  constructor(private readonly ctxMgr: GameContext) {}

  enter(): void {
    this.entered = true;
    void this.reload();
    this.rebuild();
  }

  exit(): void {
    this.entered = false;
    this.ctxMgr.buttons.clear();
  }

  update(dt: number): void {
    this.pulse = (this.pulse + dt) % 1;
    if (this.toastT > 0) this.toastT -= dt;
  }

  private async reload(): Promise<void> {
    if (!this.ctxMgr.settings.isLoggedIn()) return;
    await this.ctxMgr.daily.refresh(this.ctxMgr.settings.playerName, this.ctxMgr.settings.authToken);
    if (this.entered) this.rebuild();
  }

  private async doClaim(): Promise<void> {
    if (this.claiming || !this.ctxMgr.daily.claimable()) return;
    this.claiming = true;
    const res = await this.ctxMgr.daily.claim(
      this.ctxMgr.settings.playerName,
      this.ctxMgr.settings.authToken,
    );
    this.claiming = false;
    if (res) {
      await this.ctxMgr.shop.refresh(); // reflect the new fly balance
      this.toast = `+${res.reward} flies!  Day streak: ${res.streak}`;
      this.toastT = 2.5;
      this.ctxMgr.audio.play("score");
    } else {
      this.toast = "Already claimed today.";
      this.toastT = 2.5;
      await this.ctxMgr.daily.refresh(
        this.ctxMgr.settings.playerName,
        this.ctxMgr.settings.authToken,
      );
    }
    if (this.entered) this.rebuild();
  }

  private rebuild(): void {
    const buttons: Button[] = [
      backButton(() => this.ctxMgr.goTo(new MenuScene(this.ctxMgr))),
    ];
    if (this.ctxMgr.daily.claimable()) {
      buttons.push({
        ...CLAIM_BTN,
        draw: (ctx, hit) => this.drawClaimButton(ctx, hit),
        onPress: () => void this.doClaim(),
      });
    }
    this.ctxMgr.buttons.set(buttons);
  }

  render(ctx: CanvasRenderingContext2D, _alpha: number): void {
    drawSceneBackground(ctx);

    drawSceneTitle(ctx, "DAILY REWARD");

    if (!this.ctxMgr.settings.isLoggedIn()) {
      ctx.font = "13px system-ui, sans-serif";
      ctx.fillText("Log in to claim daily rewards.", VIRTUAL_WIDTH / 2, VIRTUAL_HEIGHT / 2);
      return;
    }

    const status = this.ctxMgr.daily.get();
    const streak = status?.streak ?? 1;
    const claimable = status?.claimable ?? false;
    // Day within the 7-cycle that today's claim lands on.
    const currentIndex = ((streak - 1) % DAILY_CYCLE + DAILY_CYCLE) % DAILY_CYCLE;

    ctx.font = "13px system-ui, sans-serif";
    ctx.fillStyle = "#f0d98a";
    const streakShown = claimable ? Math.max(0, streak - 1) : streak;
    ctx.fillText(
      streakShown > 0 ? `🔥 ${streakShown}-day streak` : "Start your streak today!",
      VIRTUAL_WIDTH / 2,
      72,
    );

    for (let i = 0; i < DAILY_CYCLE; i += 1) {
      const done = i < currentIndex || (!claimable && i === currentIndex);
      const current = claimable && i === currentIndex;
      this.drawCell(ctx, i, done, current);
    }

    if (!claimable) {
      ctx.fillStyle = "#9fe6a0";
      ctx.font = "bold 15px system-ui, sans-serif";
      ctx.fillText("Claimed ✓ — come back tomorrow!", VIRTUAL_WIDTH / 2, CLAIM_BTN.y + 30);
    }

    if (this.toastT > 0) {
      ctx.globalAlpha = Math.min(1, this.toastT);
      ctx.fillStyle = "#9fe6a0";
      ctx.font = "bold 16px system-ui, sans-serif";
      ctx.fillText(this.toast, VIRTUAL_WIDTH / 2, VIRTUAL_HEIGHT - 60);
      ctx.globalAlpha = 1;
    }
  }

  private drawCell(ctx: CanvasRenderingContext2D, i: number, done: boolean, current: boolean): void {
    const { x, y } = cellRect(i);
    const glow = current ? 0.5 + 0.5 * Math.sin(this.pulse * Math.PI * 2) : 0;
    ctx.fillStyle = current ? "#244b2a" : done ? "#2a3140" : "#222838";
    fillRoundedRect(ctx, x, y, CELL_W, CELL_H, 10);
    ctx.strokeStyle = current ? `rgba(159,230,160,${0.5 + glow * 0.5})` : done ? "#3a6a40" : "#3a4256";
    ctx.lineWidth = current ? 3 : 1.5;
    strokeRoundedRect(ctx, x + 0.5, y + 0.5, CELL_W - 1, CELL_H - 1, 10);

    ctx.textAlign = "center";
    ctx.fillStyle = current ? "#d8f5d8" : "#9fb0c8";
    ctx.font = "bold 11px system-ui, sans-serif";
    ctx.fillText(`Day ${i + 1}`, x + CELL_W / 2, y + 18);

    ctx.fillStyle = current ? "#9fe6a0" : done ? "#7fae84" : "#c8b88a";
    ctx.font = "bold 18px system-ui, sans-serif";
    ctx.fillText(`${DAILY_REWARDS[i]}`, x + CELL_W / 2, y + 44);
    ctx.font = "9px system-ui, sans-serif";
    ctx.fillStyle = "#8a93a8";
    ctx.fillText("flies", x + CELL_W / 2, y + 56);

    if (done) {
      ctx.fillStyle = "#9fe6a0";
      ctx.font = "bold 14px system-ui, sans-serif";
      ctx.fillText("✓", x + CELL_W / 2, y + CELL_H - 8);
    }
  }

  private drawClaimButton(ctx: CanvasRenderingContext2D, hit: boolean): void {
    const reward = this.ctxMgr.daily.get()?.reward ?? 0;
    ctx.fillStyle = hit ? "#1b7a3a" : "#2e7d32";
    fillRoundedRect(ctx, CLAIM_BTN.x, CLAIM_BTN.y, CLAIM_BTN.w, CLAIM_BTN.h, 12);
    ctx.fillStyle = "#fff";
    ctx.font = "bold 18px system-ui, sans-serif";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText(`Claim ${reward} flies`, CLAIM_BTN.x + CLAIM_BTN.w / 2, CLAIM_BTN.y + CLAIM_BTN.h / 2);
    ctx.textBaseline = "alphabetic";
  }
}

